import { isLeft } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { AppSpotlightController } from '../useAppSpotlight';
import { MAX_SPOTLIGHT_APPS, useDraftForm } from '../useDraftForm';
import type {
  ExperimentPrefill,
  Spotlight,
  SpotlightSavePayload,
} from '../types';
import { fromDateTimeLocal } from '../utils';
import { ConfirmDialog } from './ConfirmDialog';
import { CuratePanel } from './CuratePanel';
import { LifecycleHeader } from './LifecycleHeader';

const MIN_PUBLISHED_APPS = 4;

interface SpotlightDetailProps {
  spotlight: Spotlight | null;
  controller: AppSpotlightController;
  onSaved: (spotlight: Spotlight) => void;
  onRunExperiment: (prefill: ExperimentPrefill) => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function SpotlightDetail({
  spotlight,
  controller,
  onSaved,
  onRunExperiment,
  onDirtyChange,
}: SpotlightDetailProps) {
  const draft = useDraftForm(spotlight);
  const { form } = draft;

  const [editingPublished, setEditingPublished] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  const status = spotlight?.status ?? null;
  const isPublished = status === 'published';
  const readOnly = status === 'archived' || (isPublished && !editingPublished);
  const weekLocked = isPublished;
  const archiveConfirmationTitle = (
    spotlight?.themeTitle ?? form.themeTitle
  ).trim();

  useEffect(() => {
    onDirtyChange(draft.isDirty);
    return () => onDirtyChange(false);
  }, [draft.isDirty, onDirtyChange]);

  const buildPayload = (): SpotlightSavePayload | null => {
    if (!form.weekId.trim()) {
      toast.error('Week ID is required');
      return null;
    }
    if (!form.themeTitle.trim()) {
      toast.error('Theme title is required');
      return null;
    }
    if (!form.headerBlurb.trim()) {
      toast.error('Header blurb is required');
      return null;
    }

    const publishStartAt = fromDateTimeLocal(form.publishStartAt);
    const publishEndAt = fromDateTimeLocal(form.publishEndAt);
    if (!publishStartAt || !publishEndAt) {
      toast.error('Publish start and end are required');
      return null;
    }
    if (new Date(publishEndAt) <= new Date(publishStartAt)) {
      toast.error('Publish end must be after publish start');
      return null;
    }

    const appEntries = form.appEntries
      .map((entry) => {
        const note = (entry.curationNote ?? '').trim();
        const reviewText = entry.featuredReview?.text.trim() ?? '';
        return {
          androidPackage: entry.androidPackage.trim(),
          curationNote: note || null,
          featuredReview:
            entry.featuredReview && reviewText
              ? { ...entry.featuredReview, text: reviewText }
              : null,
        };
      })
      .filter((entry) => entry.androidPackage.length > 0);

    return {
      id: form.id,
      weekId: form.weekId.trim(),
      themeTitle: form.themeTitle.trim(),
      headerBlurb: form.headerBlurb.trim(),
      publishStartAt,
      publishEndAt,
      appEntries,
      sourceCandidateRunId: form.sourceCandidateRunId,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) {
      return;
    }

    const result = await controller.saveSpotlight(payload);
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }

    toast.success(form.id ? 'Spotlight updated' : 'Spotlight created');
    if (form.id) {
      draft.reseed(result.right);
    }
    onSaved(result.right);
  };

  const requestUpdateLive = () => {
    const payload = buildPayload();
    if (!payload) {
      return;
    }
    if (payload.appEntries.length < MIN_PUBLISHED_APPS) {
      toast.error(
        `A published spotlight needs at least ${MIN_PUBLISHED_APPS} apps`,
      );
      return;
    }
    if (payload.appEntries.length > MAX_SPOTLIGHT_APPS) {
      toast.error(
        `A published spotlight can include at most ${MAX_SPOTLIGHT_APPS} apps`,
      );
      return;
    }
    setConfirmOpen(true);
  };

  const handleUpdateLive = async () => {
    const payload = buildPayload();
    if (!payload) {
      return;
    }

    const result = await controller.updatePublishedSpotlight(payload);
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }

    toast.success('Live spotlight updated');
    draft.reseed(result.right);
    setEditingPublished(false);
    onSaved(result.right);
  };

  const handleStatus = async (
    op: 'publish' | 'archive' | 'unarchive',
    successMessage: string,
    confirmationText?: string,
  ) => {
    if (!form.id) {
      toast.error('Save the spotlight first');
      return;
    }
    const result = await controller.setStatus(form.id, op, confirmationText);
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }
    toast.success(successMessage);
    onSaved(result.right);
  };

  const requestArchive = () => {
    if (isPublished) {
      setArchiveConfirmOpen(true);
      return;
    }
    void handleStatus('archive', 'Spotlight archived');
  };

  const handleGenerate = async () => {
    if (!form.weekId.trim()) {
      toast.error('Week ID is required');
      return;
    }
    const result = await controller.generateCandidates({
      weekId: form.weekId.trim(),
      themeTitle: form.themeTitle.trim() || null,
      excludedAndroidPackages: form.appEntries.map(
        (entry) => entry.androidPackage,
      ),
    });
    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }
    toast.success('Suggestions generated');
  };

  const cancelEdit = () => {
    if (spotlight) {
      draft.reseed(spotlight);
    }
    setEditingPublished(false);
  };

  return (
    <div className="space-y-4">
      <LifecycleHeader
        status={status}
        weekId={form.weekId}
        themeTitle={form.themeTitle}
        appCount={form.appEntries.length}
        isDirty={draft.isDirty}
        isSaving={controller.isSaving}
        isEditingPublished={editingPublished}
        onSave={() => void handleSave()}
        onPublish={() => void handleStatus('publish', 'Spotlight published')}
        onArchive={requestArchive}
        onUnarchive={() =>
          void handleStatus('unarchive', 'Spotlight restored to draft')
        }
        onRunExperiment={() =>
          onRunExperiment({
            weekId: form.weekId.trim(),
            draftId: form.id,
            whitelistAndroidPackages: form.appEntries.map(
              (entry) => entry.androidPackage,
            ),
          })
        }
        onEditPublished={() => setEditingPublished(true)}
        onCancelEdit={cancelEdit}
        onUpdateLive={requestUpdateLive}
      />

      <CuratePanel
        draft={draft}
        appOptions={controller.appOptions}
        isLoadingApps={controller.isLoadingApps}
        readOnly={readOnly}
        weekLocked={weekLocked}
        candidateRuns={controller.candidateRuns}
        isGenerating={controller.isGenerating}
        loadReviews={controller.fetchDappReviews}
        onGenerateCandidates={() => void handleGenerate()}
      />

      {confirmOpen && (
        <ConfirmDialog
          title="Update the live spotlight?"
          message="These changes regenerate the published artifact and are visible to users immediately."
          confirmLabel="Update live"
          isBusy={controller.isSaving}
          onConfirm={() => {
            setConfirmOpen(false);
            void handleUpdateLive();
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {archiveConfirmOpen && (
        <ConfirmDialog
          title="Archive the live spotlight?"
          message="Archiving a published spotlight removes it from the live App Spotlight response."
          confirmLabel="Archive live"
          requiredConfirmation={archiveConfirmationTitle}
          isDestructive
          isBusy={controller.isSaving}
          onConfirm={(confirmationText) => {
            setArchiveConfirmOpen(false);
            void handleStatus(
              'archive',
              'Spotlight archived',
              confirmationText,
            );
          }}
          onCancel={() => setArchiveConfirmOpen(false)}
        />
      )}
    </div>
  );
}
